import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Divider, List, Text, TouchableRipple } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  AppBottomSheet,
  AppSearchbar,
  BottomSheetScrollView,
  NotificationDot,
} from '@/components/atoms';
import { useTranslation } from '@/i18n';
import { colorSemantic, rippleOnSurface, spacing, unisonColors, useAppTheme } from '@/theme';

const SHEET_SNAP_POINTS = ['50%', '90%'] as const;
const ROW_HEIGHT = 56;
const UNREAD_DOT_SIZE = 9;

export interface ContactFilterOption {
  readonly id: string;
  readonly label: string;
}

interface ContactFilterSheetProps {
  readonly visible: boolean;
  readonly title: string;
  readonly allOptionLabel: string;
  readonly contacts: readonly ContactFilterOption[];
  readonly selectedContactId: string | null;
  readonly unreadContactIds?: readonly string[];
  readonly loading: boolean;
  readonly onSearchChange: (search: string) => void;
  readonly onSelect: (contactId: string | null) => void;
  readonly onDismiss: () => void;
}

interface SheetItemProps {
  readonly title: string;
  readonly selected: boolean;
  readonly showDivider: boolean;
  readonly showDot?: boolean;
  readonly onPress: () => void;
  readonly testID: string;
}

const SheetItem = ({
  title,
  selected,
  showDivider,
  showDot = false,
  onPress,
  testID,
}: SheetItemProps) => {
  const { colors } = useAppTheme();

  return (
    <View style={styles.row}>
      <TouchableRipple
        accessibilityLabel={title}
        accessibilityRole="button"
        rippleColor={rippleOnSurface}
        style={[styles.touchable, selected ? styles.selectedTouchable : undefined]}
        testID={testID}
        onPress={onPress}>
        <View style={styles.rowContent}>
          <View style={styles.labelGroup}>
            <Text numberOfLines={1} style={[styles.rowTitle, { color: colors.onSurface }]}>
              {title}
            </Text>
            {showDot ? (
              <NotificationDot
                color={colorSemantic.colorBgErrorStrong}
                size={UNREAD_DOT_SIZE}
                testID={`${testID}-dot`}
              />
            ) : null}
          </View>
          {selected ? (
            <View testID={`${testID}-selected-icon`}>
              <List.Icon icon="check" color={unisonColors.colorIconBrandStrong} />
            </View>
          ) : null}
        </View>
      </TouchableRipple>
      {showDivider ? <Divider /> : null}
    </View>
  );
};

export const ContactFilterSheet = ({
  visible,
  title,
  allOptionLabel,
  contacts,
  selectedContactId,
  unreadContactIds = [],
  loading,
  onSearchChange,
  onSelect,
  onDismiss,
}: ContactFilterSheetProps) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [search, setSearch] = useState('');

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      onSearchChange(value);
    },
    [onSearchChange],
  );

  const handleSelectAll = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const rows = useMemo(
    () =>
      contacts.map((contact, index) => (
        <SheetItem
          key={contact.id}
          title={contact.label}
          selected={contact.id === selectedContactId}
          showDivider={index < contacts.length - 1}
          showDot={unreadContactIds.includes(contact.id)}
          testID={`contact-filter-sheet-item-${contact.id}`}
          onPress={() => onSelect(contact.id)}
        />
      )),
    [contacts, onSelect, selectedContactId, unreadContactIds],
  );

  return (
    <AppBottomSheet visible={visible} snapPoints={[...SHEET_SNAP_POINTS]} onDismiss={onDismiss}>
      <View style={styles.header}>
        <Text variant="titleLarge">{title}</Text>
      </View>
      <Divider />
      <View style={styles.searchContainer}>
        <AppSearchbar
          placeholder={t('aiTasks.filter.searchContactPlaceholder')}
          testID="contact-filter-sheet-search"
          value={search}
          onChangeText={handleSearchChange}
        />
      </View>
      <SheetItem
        title={allOptionLabel}
        selected={selectedContactId === null}
        showDivider
        testID="contact-filter-sheet-item-all"
        onPress={handleSelectAll}
      />
      {loading ? (
        <ActivityIndicator size="small" style={styles.loading} />
      ) : (
        <BottomSheetScrollView
          contentContainerStyle={{ paddingBottom: insets.bottom }}
          style={styles.scrollView}
          testID="contact-filter-sheet-scroll">
          {rows}
        </BottomSheetScrollView>
      )}
    </AppBottomSheet>
  );
};

const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    paddingVertical: spacing[2],
  },
  searchContainer: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
  },
  loading: {
    padding: spacing[8],
  },
  scrollView: {
    flex: 1,
  },
  row: {
    justifyContent: 'center',
    minHeight: ROW_HEIGHT,
    width: '100%',
  },
  touchable: {
    justifyContent: 'center',
    width: '100%',
  },
  selectedTouchable: {
    backgroundColor: unisonColors.brandGreenWeak,
  },
  rowContent: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: ROW_HEIGHT,
    paddingHorizontal: spacing[4],
  },
  labelGroup: {
    alignItems: 'center',
    columnGap: spacing[2],
    flex: 1,
    flexDirection: 'row',
  },
  rowTitle: {
    flexShrink: 1,
    fontSize: 16,
    lineHeight: 24,
  },
});
